/**
 * Deterministic pseudo-randomness for the public `/demo` fixtures.
 *
 * The whole demo rests on one property: the same filter state must produce the
 * same numbers, forever. Two reasons, and neither is cosmetic.
 *
 * 1. The old fixtures called `Math.random()` at module load
 *    (`data/sampleReportData.ts`), so every refresh redrew the charts. A visitor
 *    who reloads and sees different "real" data learns the product is lying,
 *    and screenshots for ads or docs could never be reproduced.
 * 2. Tests can assert exact payloads instead of loose ranges, which is what
 *    makes "changing the time range changes the numbers" testable at all.
 *
 * So: no `Math.random()` and no `Date.now()` anywhere under `lib/demo/`. Both
 * are banned by eslint and by a source-scanning test. Callers derive every
 * value from a canonical key string via `rngFor()`.
 */

/**
 * Fixed point in time the whole demo is anchored to, floored to UTC midnight.
 *
 * Flooring is load-bearing. If timestamps were derived from "now", the same
 * seed key would produce different ISO date strings either side of midnight,
 * and the determinism test would fail once a day in whichever timezone CI
 * happened to run in. A frozen anchor also means the demo's "last 30 days"
 * never drifts into a weekend-shaped hole.
 */
export const DEMO_ANCHOR_DATE = new Date(Date.UTC(2026, 8, 1));

/**
 * cyrb53 - a well-known small non-cryptographic string hash with good
 * avalanche behaviour. Used only to turn a key string into a 32-bit seed, so
 * collision resistance beyond "different keys look different" is irrelevant.
 */
export function cyrb53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/** mulberry32 - 32-bit PRNG, uniform in [0, 1). All state lives in the closure. */
export function mulberry32(a: number): () => number {
  let t = a >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let x = Math.imul(t ^ (t >>> 15), 1 | t);
    x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

/** The filter state every generator is a pure function of. */
export interface DemoSeedInput {
  /** Logical route the payload is for, e.g. `metrics/dashboard`. */
  route: string;
  days: number;
  repoId?: string | null;
  teamId?: string | null;
  excludeBots: boolean;
}

/**
 * Canonical key string. Built from named fields rather than `JSON.stringify` of
 * an object, because object key order is an implementation detail and would
 * silently reseed everything the day someone reorders a literal.
 */
export function seedKey(input: DemoSeedInput, subKey?: string): string {
  const base = [
    input.route,
    `days=${input.days}`,
    `repo=${input.repoId ?? 'all'}`,
    `team=${input.teamId ?? 'all'}`,
    `bots=${input.excludeBots ? 'excluded' : 'included'}`,
  ].join('|');
  return subKey ? `${base}|${subKey}` : base;
}

/**
 * An rng scoped to one sub-series.
 *
 * Always pass a `subKey`. Each series drawing from its own stream means adding
 * a field to one payload cannot shift the numbers of an unrelated one - which
 * would otherwise turn every new metric into a churn of snapshot updates.
 */
export function rngFor(input: DemoSeedInput, subKey: string): () => number {
  return mulberry32(cyrb53(seedKey(input, subKey)));
}

/** Uniform integer in [min, max]. */
export function randInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

/** Uniform float in [min, max), rounded to `decimals`. */
export function randFloat(rng: () => number, min: number, max: number, decimals = 2): number {
  const raw = rng() * (max - min) + min;
  const factor = 10 ** decimals;
  return Math.round(raw * factor) / factor;
}

/** Symmetric jitter around 1, e.g. `jitter(rng, 0.15)` gives 0.85-1.15. */
export function jitter(rng: () => number, spread: number): number {
  return 1 + (rng() - 0.5) * 2 * spread;
}

/** Deterministic pick from a non-empty list. */
export function pick<T>(rng: () => number, items: readonly T[]): T {
  if (items.length === 0) {
    throw new Error('pick() called with an empty list');
  }
  return items[Math.floor(rng() * items.length)];
}

/** Fisher-Yates against the supplied rng. Returns a new array. */
export function shuffle<T>(rng: () => number, items: readonly T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** ISO date (YYYY-MM-DD) `daysAgo` days before the anchor. */
export function demoDate(daysAgo: number): string {
  const d = new Date(DEMO_ANCHOR_DATE);
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d.toISOString().split('T')[0];
}

/** Full ISO timestamp `hoursAgo` hours before the anchor. */
export function demoTimestamp(hoursAgo: number): string {
  const d = new Date(DEMO_ANCHOR_DATE);
  d.setUTCHours(d.getUTCHours() - hoursAgo);
  return d.toISOString();
}
